import React, { useRef, useState, useImperativeHandle, forwardRef } from "react";
import { Modal, Button, Form, Row, Col, Table, Spinner } from "react-bootstrap";
import LocationPickerMap, { LocationPickerRef } from "./LocationPickerMap";
import apiClient, { _get, _post } from "../../services/apiClient";
import { apiUrls } from "../../config/apiUrls.const";
import { showAlert, showToast } from "../../services/alertService";
import { useUser } from "../../contexts/UserContext";
import { setSecureItem } from "../../utils/LocalStorageManager";

export interface BookingFormRef {
  open: (initialService: any, mode: "scheduled" | "instant") => void;
}

interface SelectedService {
  id: number;
  title: string;
  price: number;
  quantity: number;
  currency: string;
}

const TRAVEL_FEE = 100;

export const BookingForm = forwardRef<BookingFormRef, { onSuccess?: () => void }>(({ onSuccess }, ref) => {
  const { user, setUser } = useUser();

  // --- Modals Control ---
  const [show, setShow] = useState(false);
  const [showAddMore, setShowAddMore] = useState(false);

  // --- Business Logic State ---
  const [initialMode, setInitialMode] = useState<"scheduled" | "instant">("scheduled");
  const [selectedServices, setSelectedServices] = useState<SelectedService[]>([]);
  const [business, setBusiness] = useState<any | null>(null);
  const [primaryService, setPrimaryService] = useState<any>(null);

  // customer info
  const [customerName, setCustomerName] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [email, setEmail] = useState("");

  // --- Catalog for "Add More" ---
  const [categories, setCategories] = useState<any[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [expandedSubId, setExpandedSubId] = useState<number | null>(null);
  const [subCategoryItems, setSubCategoryItems] = useState<any[]>([]);
  
  // Storing IDs strictly as strings or numbers (cleared cleanly)
  const [selectedCatId, setSelectedCatId] = useState<number | "">("");
  const [selectedSubId, setSelectedSubId] = useState<number | "">("");
  const [loadingItems, setLoadingItems] = useState(false);

  // --- Common Form Fields ---
  const [locationType, setLocationType] = useState<"center" | "home">("center");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [techPreference, setTechPreference] = useState("any");
  const [address, setAddress] = useState("");
  const [pinLocation, setPinLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [notes, setNotes] = useState("");
  const [addNewUser, setAddNewUser] = useState(false);
  const [existingUser, setExistingUser] = useState<any | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const mapRef = useRef<LocationPickerRef>(null);

  useImperativeHandle(ref, () => ({
    open: (service, mode) => {
      setBusiness(service.business);
      setPrimaryService(service);
      setInitialMode(mode);
      setLocationType(mode === "instant" ? "home" : "center");

      // Pre-fill user data if logged in
      setCustomerName(user?.name || "");
      setMobileNo(user?.mobile_no || ""); 
      setEmail(user?.email || "");

      setSelectedServices([{
        id: service.id,
        title: service.title,
        price: parseFloat(service.price),
        quantity: 1,
        currency: service.currency || "BDT"
      }]);

      setDate(""); setTime(""); setAddress(""); setPinLocation(null); setNotes("");
      setShow(true);
    },
  }));

  // STEP 1: Fetch Categories and Subcategories
  const fetchBusinessCatalog = async () => {
    if (!business) return;
    setLoadingCatalog(true);
    try {
      const response = await _get(apiUrls.businessServicesCategories, {
        business_id: business?.id,
        service_group: primaryService?.service_group
      });
      setCategories(response.data?.data || []);
      setSelectedCatId("");
      setSelectedSubId("");
      setSubCategoryItems([]);
      setShowAddMore(true);
    } catch (err) {
      showToast({ icon: "error", title: "Catalog Error", text: "Could not load categories." });
    } finally {
      setLoadingCatalog(false);
    }
  };

  // STEP 2: Fetch specific Service Items when a Subcategory is clicked
  const fetchItemsBySubCategory = async (subId: number) => {
    setLoadingItems(true);
    setExpandedSubId(subId);

    try {
      const response = await _get(apiUrls.businessServiceItems, {
        business_id: business?.id,
        category_id: selectedCatId, 
        subcategory_id: subId,     
        service_group: primaryService?.service_group
      });
      setSubCategoryItems(response.data?.data || []);
    } catch (err) {
      showToast({ icon: "error", title: "Error", text: "Failed to load services." });
    } finally {
      setLoadingItems(false);
    }
  };

  const addServiceToCart = (service: any) => {
    if (selectedServices.find(s => s.id === service.id)) {
      showToast({ icon: "info", title: "Already in list" });
      return;
    }
    setSelectedServices(prev => [...prev, {
      id: service.id,
      title: service.title,
      price: parseFloat(service.price),
      quantity: 1,
      currency: service.currency || "BDT"
    }]);
    setShowAddMore(false);
    setExpandedSubId(null);
  };

  const updateQuantity = (id: number, delta: number) => {
    setSelectedServices(prev => prev.map(s =>
      s.id === id ? { ...s, quantity: Math.max(1, s.quantity + delta) } : s
    ));
  };

  const removeService = (id: number) => {
    if (selectedServices.length > 1) {
      setSelectedServices(prev => prev.filter(s => s.id !== id));
    }
  };

  const totalServiceCharge = selectedServices.reduce((sum, s) => sum + (s.price * s.quantity), 0);
  const travelFee = locationType === "home" ? TRAVEL_FEE : 0;
  const totalPayable = totalServiceCharge + travelFee;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!mobileNo) {
      showToast({ icon: "warning", title: "Required", text: "Mobile number is required." });
      return;
    }

    if (locationType === "home" && (!address || !pinLocation)) {
      showToast({ icon: "warning", title: "Missing Info", text: "Please provide address and pin location." });
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        business_id: business?.id,
        booking_type: initialMode,
        location_type: locationType,
        technician_preference: techPreference,
        notes,
        customer_name: customerName,
        mobile_no: mobileNo,
        email: email,
        add_new_user: addNewUser,
        existing_user: existingUser,
        items: selectedServices.map((s) => ({
          business_item_id: s.id,
          quantity: s.quantity,
        })),
        ...(initialMode !== "instant" && { scheduled_date: date, scheduled_time: time }),
        ...(locationType === "home" && { address, latitude: pinLocation?.lat, longitude: pinLocation?.lng }),
      };

      let activeToken = null;

      if (!user) {
        const checkRes = await _post(`${apiUrls.checkAndGetUserByMobileNoOrEmail}`, { mobile_no: mobileNo, email: email });

        if (!checkRes.data.data) {
          const result = await showAlert({
            icon: "question",
            title: "Create an account?",
            html: `<h5>We noticed you're new!</h5> Would you like to create an account? <br/><br/> 
                   <div class="text-danger"><b>Note:</b> Your mobile number (<i>${mobileNo}</i>) will be used as your initial password.</div>`,
            showCancelButton: true,
            confirmButtonText: "Yes, Create Account",
            cancelButtonText: "No, Book as Guest",
          });

          if (result.isConfirmed) {
            const regRes = await _post(apiUrls.register, {
              name: customerName,
              mobile_no: mobileNo,
              email: email,
              password: mobileNo,
              password_confirmation: mobileNo,
            });

            if (regRes.data.status === "success") {
              setAddNewUser(true);
              const accessToken = regRes.data.data.token.access_token;
              const refreshToken = regRes.data.data.token.refresh_token;
              activeToken = accessToken;

              if (accessToken) {
                setSecureItem("access_token", accessToken);
                setSecureItem("refresh_token", refreshToken);

                const userDataResponse = await apiClient.get(apiUrls.user);
                const user = userDataResponse?.data?.data;

                if (user) {
                  setSecureItem("user", user);
                }
              }
            }
          }
        } else {
          setExistingUser(checkRes.data.data);
          console.log(payload);
        }
      }

      const config = activeToken ? { headers: { Authorization: `Bearer ${activeToken}` } } : {};
      const response = await _post(apiUrls.createBooking, payload, config);
      const result = response.data;

      if (result.success) {
        const isNewUser = result.data.new_user;
        let alertTitle = "Booking Confirmed! 🎉";
        let alertHtml = `Your booking (ID: #${result.data.booking_id}) has been placed successfully.`;

        if (isNewUser) {
          alertTitle = "Welcome to our family! ❤️";
          alertHtml = `
          <div class="text-start">
            <p>Your booking <b>#${result.data.booking_id}</b> is confirmed.</p>
            <hr/>
            <div class="p-3 bg-light rounded border border-primary">
              <h6 class="fw-bold text-primary">Account Created Successfully!</h6>
              <p class="small mb-1">We've created an account for you. Use these details to log in next time:</p>
              <ul class="small">
                <li><b>Username:</b> ${mobileNo} / ${email} / ${customerName}</li>
                <li><b>Password:</b> ${mobileNo}</li>
              </ul>
              <p class="small text-danger mb-0"><b>Action Required:</b> Please log in and update your password as soon as possible for better security.</p>
            </div>
          </div>
        `;
        }

        await showAlert({
          icon: "success",
          title: alertTitle,
          html: alertHtml,
          confirmButtonText: "Great, Thank You!",
        });

        if (onSuccess) onSuccess();
        setShow(false);
        if (isNewUser) {
          window.location.reload();
        }
      }

    } catch (err: any) {
      console.error("Booking Error:", err);
      showToast({
        icon: "error",
        title: "Submission Error",
        text: err.response?.data?.message || "Failed to process booking."
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={() => !submitting && setShow(false)} backdrop="static" centered size="lg">
        <Modal.Header closeButton={!submitting}>
          <Modal.Title className="fw-bold">{initialMode === "instant" ? "⚡ Instant Request" : "📅 Appointment"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handleSubmit}>
            <div className="mb-4 p-3 border rounded bg-light">
              <h6 className="fw-bold mb-3">Contact Information</h6>
              <Row>
                <Col md={4} className="mb-2">
                  <Form.Label className="small fw-bold">Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Guest Name"
                    value={customerName}
                    onChange={e => setCustomerName(e.target.value)}
                  />
                </Col>
                <Col md={4} className="mb-2">
                  <Form.Label className="small fw-bold">Mobile No <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="017..."
                    value={mobileNo}
                    required
                    onChange={e => setMobileNo(e.target.value)}
                  />
                </Col>
                <Col md={4} className="mb-2">
                  <Form.Label className="small fw-bold">Email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                </Col>
              </Row>
            </div>

            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-bold mb-0">Services for {business?.name}</h6>
                <Button variant="outline-primary" size="sm" onClick={fetchBusinessCatalog} disabled={loadingCatalog}>
                  {loadingCatalog ? <Spinner size="sm" /> : "+ Add Services"}
                </Button>
              </div>
              <Table responsive bordered hover className="align-middle">
                <thead className="bg-light">
                  <tr className="small text-uppercase">
                    <th>Service</th>
                    <th className="text-center">Qty/Persons</th>
                    <th className="text-end">Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {selectedServices.map(item => (
                    <tr key={item.id}>
                      <td className="small fw-bold">{item.title}</td>
                      <td style={{ width: "130px" }}>
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <Button size="sm" variant="light" className="border px-2" onClick={() => updateQuantity(item.id, -1)}>-</Button>
                          <span className="fw-bold">{item.quantity}</span>
                          <Button size="sm" variant="light" className="border px-2" onClick={() => updateQuantity(item.id, 1)}>+</Button>
                        </div>
                      </td>
                      <td className="text-end fw-bold small">{item.price * item.quantity} {item.currency}</td>
                      <td className="text-center">
                        <Button variant="link" className="text-danger p-0" onClick={() => removeService(item.id)}>&times;</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {initialMode !== "instant" && (
              <Row className="mb-3">
                <Col md={12} className="mb-3">
                  <Form.Label className="fw-bold small text-muted text-uppercase">Location</Form.Label>
                  <div className="d-flex gap-2">
                    <Button variant={locationType === "center" ? "dark" : "outline-dark"} className="flex-grow-1" onClick={() => setLocationType("center")}>🏢 Center</Button>
                    <Button variant={locationType === "home" ? "dark" : "outline-dark"} className="flex-grow-1" onClick={() => setLocationType("home")}>🏠 Home</Button>
                  </div>
                </Col>
                <Col md={6}><Form.Label className="small fw-bold">Date</Form.Label><Form.Control type="date" value={date} onChange={e => setDate(e.target.value)} /></Col>
                <Col md={6}><Form.Label className="small fw-bold">Time</Form.Label><Form.Control type="time" value={time} onChange={e => setTime(e.target.value)} /></Col>
              </Row>
            )}

            {locationType === "home" && (
              <div className="p-3 bg-light rounded border mb-3">
                <Form.Group className="mb-2">
                  <Form.Label className="fw-bold small">Address</Form.Label>
                  <Form.Control as="textarea" rows={2} value={address} onChange={e => setAddress(e.target.value)} />
                </Form.Group>
                <LocationPickerMap ref={mapRef} mode="create" value={pinLocation} height="180px" onChange={setPinLocation} />
              </div>
            )}

            <div className="p-3 bg-light rounded border mb-3">
              <Form.Group className="mb-2">
                <Form.Label className="fw-bold small">Note</Form.Label>
                <Form.Control as="textarea" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
              </Form.Group>
            </div>

            <div className="p-3 bg-light border-start border-primary border-4 rounded mb-4">
              <div className="d-flex justify-content-between small mb-1">
                <span>Subtotal ({selectedServices.length} items):</span>
                <span>{totalServiceCharge} {selectedServices[0]?.currency}</span>
              </div>
              {locationType === "home" && <div className="d-flex justify-content-between small text-success"><span>Travel Fee:</span><span>+ {TRAVEL_FEE} BDT</span></div>}
              <hr className="my-2" />
              <div className="d-flex justify-content-between fw-bold fs-5">
                <span>Total Payable:</span>
                <span className="text-primary">{totalPayable} {selectedServices[0]?.currency}</span>
              </div>
            </div>

            <Button type="submit" variant="primary" size="lg" className="w-100 fw-bold" disabled={submitting}>
              {submitting ? <Spinner size="sm" /> : "CONFIRM REQUEST"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* --- ADD MORE SERVICES MODAL (Categorized) --- */}
      <Modal show={showAddMore} onHide={() => setShowAddMore(false)} centered size="sm" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fw-bold">Find Services</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 bg-white">
          {/* 1. Category Selection */}
          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold text-muted">Step 1: Select Category</Form.Label>
            <Form.Select
              value={selectedCatId}
              onChange={(e) => {
                const val = e.target.value;
                // Parse to number if a valid value is selected, otherwise reset to ""
                setSelectedCatId(val ? Number(val) : ""); 
                setSelectedSubId("");
                setSubCategoryItems([]);
              }}
            >
              <option value="">Choose a category...</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.st_category.id}>
                  {cat.st_category.name_en}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* 2. Subcategory Selection */}
          {selectedCatId !== "" && (
            <Form.Group className="mb-4">
              <Form.Label className="small fw-bold text-muted">Step 2: Select Sub-Category</Form.Label>
              <Form.Select
                value={selectedSubId}
                onChange={(e) => {
                  const subId = e.target.value;
                  if (subId) {
                    const parsedSubId = Number(subId); // Force to number matching your API payload
                    setSelectedSubId(parsedSubId);
                    fetchItemsBySubCategory(parsedSubId);
                  } else {
                    setSelectedSubId("");
                    setSubCategoryItems([]);
                  }
                }}
              >
                <option value="">Choose a sub-category...</option>
                {categories
                  .find(c => Number(c.st_category.id) === selectedCatId) // Crucial type match fix
                  ?.st_subcategories.map((sub: any) => (
                    <option key={sub.id} value={sub.st_subcategory.id}>
                      {sub.st_subcategory.name_en} {/* Outputs: "Haircuts & Trims", "Hairstyling", etc. */}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          )}

          {/* 3. Items Result List */}
          <div className="mt-2">
            {loadingItems ? (
              <div className="text-center py-4"><Spinner size="sm" variant="primary" /></div>
            ) : subCategoryItems.length > 0 ? (
              <>
                <label className="small fw-bold text-muted mb-2">Step 3: Add to Booking</label>
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {subCategoryItems.map((svc) => (
                    <div key={svc.id} className="d-flex justify-content-between align-items-center p-2 bg-light mb-2 border rounded">
                      <div className="small">
                        <div className="fw-bold">{svc.title}</div>
                        <div className="text-primary">{svc.price} BDT</div>
                      </div>
                      <Button
                        variant={selectedServices.find(s => s.id === svc.id) ? "success" : "primary"}
                        size="sm"
                        onClick={() => addServiceToCart(svc)}
                      >
                        {selectedServices.find(s => s.id === svc.id) ? "✓ Added" : "+ Add"}
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            ) : selectedSubId && (
              <div className="text-center py-4 text-muted small border rounded bg-light">
                No specific items found in this sub-category.
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
});

BookingForm.displayName = "BookingForm";